import {useSignal} from '@preact/signals-react'
import {useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import {Button} from 'primereact/button'
import {Chip} from 'primereact/chip'
import {Chips} from 'primereact/chips'
import {InputText} from 'primereact/inputtext'
import {OverlayPanel} from 'primereact/overlaypanel'
import {Sidebar} from 'primereact/sidebar'
import {Tag} from 'primereact/tag'
import {forwardRef, useImperativeHandle, useMemo, useRef} from 'react'
import TranslateUtils from '../../i18n/TranslateUtils'
import {QueryKeyTypes} from '../../query-manager/QueryKeyTypes'
import {
    QueryCRUDTypes,
    QueryKeyManager,
} from '../../query-manager/QueryKeysManager'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../utils/BlockScrollsUtils'
import {checkEmptyString} from '../../utils/CommonUtils'
import {useAuth} from '../authentication/AuthProvider'
import {component, getComposedDataList} from '../form/Constant'
import FormHeader from '../form/FormHeader'
import ImageUploader from '../image/ImageUploader'
import PageContentWrapper from '../layout/PageContentWrapper'
import {severityNotification} from '../notification/NotificationConstant'
import {showNotification} from '../notification/NotificationService'
import {isArrayAndNotEmpty} from '../observations/Utils'
import {convertKeyToLabel} from '../table-utils/TableUtils'
import {
    pathwayKeys,
    pathwayList,
    styledPathwayList,
    symptomSuggestionList,
} from './PathwayConstants'
import {usePathwayCRUD} from './query-keys/PathwayKeysManager'
import './style/style.css'

const StyledPathwayForm = forwardRef((props, ref) => {
    useSignals()
    useImperativeHandle(ref, () => ({
        showForm: handleShowForm,
        hideForm: handleHideForm,
    }))
    //#region Translate
    const {translateDatatable, translateKeys, translateCommons} =
        TranslateUtils()
    //#endregion

    //#region Context
    const {user} = useAuth()
    //#endregion

    //#region State
    const visible = useSignal(false)
    const data = useSignal({})
    const filterBySuggestion = useSignal('')
    const overplayWidth = useSignal(null)
    //#endregion

    //#region Ref
    const suggestionRef = useRef(null)
    const chipRef = useRef(null)
    const chipInputRef = useRef(null)
    //#endregion

    //#region Memo
    const dataList = useMemo(() => {
        return getComposedDataList(pathwayList, data.value)
    }, [data.value])
    //#endregion

    //#region Query
    const queryClient = useQueryClient()

    const {mutateAsync: createOrUpdatePathway} = usePathwayCRUD(
        !!data.value?.id ? QueryCRUDTypes.update : QueryCRUDTypes.create,
        user?.id,
    )
    //#endregion

    //#region Handle Function
    const handleShowForm = value => {
        blockOverflownScroll()

        data.value = value && Object.keys(value)?.length > 0 ? {...value} : {}
        visible.value = true
    }

    const handleHideForm = () => {
        unblockOverflownScroll()
        data.value = {}
        visible.value = false
    }

    const handleOnSubmit = async () => {
        // Validate
        let isEmptyName = checkEmptyString(data.value?.name)
        if (!!isEmptyName) {
            showNotification(severityNotification.warn, 'Name is required !')
            return
        }

        // Post Data
        let toPostData = {
            ...data.value,
            id: data.value?.id || null,
            icon: data.value?.icon ? data.value.icon : null,
            symptomsBean: data.value?.symptoms ? data.value?.symptoms : [],
            createdAt: null,
        }

        const pathwayData = await createOrUpdatePathway(toPostData)

        if (pathwayData?.id) {
            const {pathwayKeys} = QueryKeyManager(
                QueryKeyTypes.pathway,
                user?.id,
            )

            queryClient.invalidateQueries({
                queryKey: pathwayKeys.pattern(),
            })

            handleHideForm()
        }
    }
    //#endregion

    const showOverlay = e => {
        console.log(chipInputRef.current)
        if (suggestionRef.current) {
            let width = e.target.offsetWidth
            overplayWidth.value = width
            if (suggestionRef.current) {
                suggestionRef.current.show(e)
            }
        }
    }

    const handleOnChipKeyDown = value => {
        filterBySuggestion.value = value
    }

    const suggestionSymptom = () => {
        let list = symptomSuggestionList
        if (filterBySuggestion.value) {
            const filteredSymptoms = symptomSuggestionList
                .map(group => {
                    const filteredValues = group.value.filter(symptom =>
                        symptom
                            .toLowerCase()
                            .includes(filterBySuggestion.value.toLowerCase()),
                    )

                    if (filteredValues.length > 0) {
                        return {
                            label: group.label,
                            value: filteredValues,
                        }
                    }
                    return null
                })
                .filter(group => group !== null)
            list = filteredSymptoms
        }
        const overlaySuggestion = document.getElementById(
            'custom-p-overlaypanel',
        )

        if (overlaySuggestion) {
            if (!isArrayAndNotEmpty(list)) {
                overlaySuggestion.classList.add('hidden')
            } else {
                overlaySuggestion.classList.remove('hidden')
            }
        }

        return list
    }

    const handleOnSuggestClick = symptom => {
        let isChecked = isArrayAndNotEmpty(data.value[pathwayKeys.symptoms])
            ? data.value[pathwayKeys.symptoms].includes(symptom)
            : false
        let symptomValue = isArrayAndNotEmpty(data.value[pathwayKeys.symptoms])
            ? [...data.value[pathwayKeys.symptoms]]
            : []
        if (isChecked) {
            symptomValue = symptomValue.filter(x => x !== symptom)
        } else {
            symptomValue.push(symptom)
        }
        data.value = {...data.value, [pathwayKeys.symptoms]: symptomValue}
    }
    const handleOnImage = (key, imageUrls) => {
        data.value = {...data.value, [pathwayKeys.icon]: imageUrls}
    }

    const handleOnChipAdd = (key, value) => {
        let holderValue = isArrayAndNotEmpty(data.value[key])
            ? [...data.value[key]]
            : []
        if (isArrayAndNotEmpty(value)) {
            holderValue = [...holderValue, ...value]
        }
        data.value = {...data.value, [key]: [...new Set(holderValue)]}
    }

    const onRemoveChip = (key, value) => {
        let holderValue = isArrayAndNotEmpty(data.value[key])
            ? [...data.value[key]]
            : []
        if (isArrayAndNotEmpty(holderValue)) {
            holderValue = holderValue.filter(x => x !== value)
        }
        data.value = {...data.value, [key]: [...new Set(holderValue)]}
    }

    return (
        <Sidebar
            className="p-sidebar-full"
            visible={visible.value}
            onHide={handleHideForm}
            blockScroll
            fullScreen>
            <div className="py-3">
                <PageContentWrapper>
                    <FormHeader
                        header={translateDatatable('PATHWAY FORM')}
                        hideMargin={true}
                        onHide={handleHideForm}
                    />
                    <div style={{minHeight: 'calc(100vh - 15rem)'}}>
                        <div className="grid relative">
                            <div className="col-12 md:col-4">
                                <div className="sticky top-0">
                                    <h4 className="mb-1 font-bold">
                                        {translateKeys('Pathway')}
                                    </h4>
                                    <hr className="highlighter" />
                                    <div className="flex flex-column gap-1">
                                        <p className="m-0 p-0 text-md font-semibold text-600">
                                            {translateKeys(
                                                'Fill in the following information to create a path way',
                                            )}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 md:col-8">
                                {isArrayAndNotEmpty(pathwayList) &&
                                    styledPathwayList.map((item, childIdx) => (
                                        <div className="col-12" key={childIdx}>
                                            <div className="flex justify-content-between align-items-start">
                                                <h5
                                                    className={
                                                        'font-bold p-0 text-sm sm:text-sm md:text-xl lg:text-xl'
                                                    }>
                                                    {translateKeys(
                                                        convertKeyToLabel(
                                                            item.key,
                                                        ),
                                                    )}
                                                    {item.shorten && (
                                                        <span>
                                                            {' '}
                                                            ({' '}
                                                            {translateKeys(
                                                                item.shorten,
                                                            )}{' '}
                                                            )
                                                        </span>
                                                    )}
                                                </h5>

                                                {!!item?.required && (
                                                    <Tag
                                                        value={translateCommons(
                                                            'This field is required !',
                                                        )}
                                                        severity="danger"
                                                        icon="pi pi-info-circle"></Tag>
                                                )}
                                            </div>
                                            <div>
                                                {item.component ===
                                                component.inputText ? (
                                                    <InputText
                                                        id={item.key}
                                                        className={`col-12 h-3rem prevent-select`}
                                                        value={
                                                            data.value[item.key]
                                                        }
                                                        placeholder={translateKeys(
                                                            item.placeholder,
                                                        )}
                                                        onChange={e =>
                                                            (data.value = {
                                                                ...data.value,
                                                                [item.key]:
                                                                    e.target
                                                                        .value,
                                                            })
                                                        }
                                                    />
                                                ) : item.component ===
                                                  component.pathway ? (
                                                    <div>
                                                        <Chips
                                                            ref={chipRef}
                                                            inputRef={
                                                                chipInputRef
                                                            }
                                                            placeholder={
                                                                item.placeholder
                                                            }
                                                            allowDuplicate={
                                                                false
                                                            }
                                                            onChange={e =>
                                                                handleOnChipAdd(
                                                                    item.key,
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            onFocus={
                                                                showOverlay
                                                            }
                                                            onKeyDown={e =>
                                                                handleOnChipKeyDown(
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            className="custom-chip w-full"
                                                        />
                                                        <div className="mt-2">
                                                            {isArrayAndNotEmpty(
                                                                data.value[
                                                                    item.key
                                                                ],
                                                            ) &&
                                                                data.value[
                                                                    item.key
                                                                ].map(
                                                                    (
                                                                        symptom,
                                                                        sIdx,
                                                                    ) => (
                                                                        <Chip
                                                                            key={
                                                                                symptom +
                                                                                sIdx
                                                                            }
                                                                            className="mb-1 mr-1 bg-teal-50 text-darkcyan border-round-lg font-semibold"
                                                                            label={
                                                                                symptom
                                                                            }
                                                                            removable
                                                                            onRemove={() => {
                                                                                onRemoveChip(
                                                                                    item.key,
                                                                                    symptom,
                                                                                )
                                                                            }}
                                                                        />
                                                                    ),
                                                                )}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    item.component ===
                                                        component.fileUpload && (
                                                        <ImageUploader
                                                            id={item.key}
                                                            className={`col-12`}
                                                            initialImage={
                                                                data.value[
                                                                    item.key
                                                                ]
                                                            }
                                                            callBack={
                                                                handleOnImage
                                                            }
                                                            multiple={
                                                                !!item?.multiple
                                                            }
                                                            extraData={
                                                                !!item?.extraData
                                                            }
                                                        />
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    </div>

                    {/* <CustomForm
                    dataList={dataList}
                    layout={formLayout.group}
                    onSubmit={handleOnSubmit}
                /> */}
                    <Button
                        className="col-12 h-3rem"
                        label="SUBMIT"
                        onClick={() => handleOnSubmit()}
                    />
                </PageContentWrapper>
                <OverlayPanel
                    id="custom-p-overlaypanel"
                    className="h-25rem overflow-scroll"
                    ref={suggestionRef}
                    style={{width: `${overplayWidth}px`}}>
                    {suggestionSymptom().map((item, idx) => (
                        <div key={idx} className="mb-2">
                            <h5 className="mb-2 font-semibold text-600">
                                {translateCommons(item.label)}
                            </h5>
                            {item.value &&
                                item.value.map((symptom, childIdx) => {
                                    const isIncluded = isArrayAndNotEmpty(
                                        data.value[pathwayKeys.symptoms],
                                    )
                                        ? data.value[
                                              pathwayKeys.symptoms
                                          ].includes(symptom)
                                        : false
                                    return (
                                        <div
                                            key={childIdx}
                                            className={`flex mb-1 hover:bg-gray-100 p-2 gap-2 ${
                                                isIncluded && 'text-darkcyan'
                                            }`}
                                            onClick={() =>
                                                handleOnSuggestClick(symptom)
                                            }>
                                            <i
                                                className={`${
                                                    isIncluded && 'pi pi-check'
                                                } w-1rem text-sm flex align-items-center`}
                                            />
                                            <span className="m-0 p-0 font-normal text-lg ">
                                                {symptom}
                                            </span>
                                        </div>
                                    )
                                })}
                            <hr />
                        </div>
                    ))}
                </OverlayPanel>
            </div>
        </Sidebar>
    )
})

export default StyledPathwayForm
